import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Loader2, BookMarked, Search, FolderTree, Sparkles, X, FileUp, Download, FileSpreadsheet, FileText, CopyX, TrendingUp, Filter, HeartPulse } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { saveAs } from 'file-saver';
import { useAuth } from '../context/AuthContext';
import { BlockRow } from '../components/rfp/BlockRow';
import { BlockDetailDrawer } from '../components/rfp/BlockDetailDrawer';
import { CategoryTreeSidebar } from '../components/rfp/CategoryTreeSidebar';
import { BlockEditorDialog } from '../components/rfp/BlockEditorDialog';
import { CategoryManagerDialog } from '../components/rfp/CategoryManagerDialog';
import { ImportWizardDialog } from '../components/rfp/ImportWizardDialog';
import { CleanupDialog } from '../components/rfp/CleanupDialog';
import { HealthDialog } from '../components/rfp/HealthDialog';
import { AiJobsChip } from '../components/rfp/AiJobsChip';
import { DuplicatesDialog } from '../components/rfp/DuplicatesDialog';
import { UsageTrendsDialog } from '../components/rfp/UsageTrendsDialog';
import { BlockHistoryDialog } from '../components/rfp/BlockHistoryDialog';
import { PromotionsQueueDialog } from '../components/rfp/PromotionsQueueDialog';
import { ScrubDialog } from '../components/rfp/ScrubDialog';
import { OverrideStatsDialog } from '../components/rfp/OverrideStatsDialog';
import { flattenTree, descendantIds } from '../components/rfp/categoryUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 30;
const SORTS = [
  { id: 'capid', label: 'Capability ID' },
  { id: 'usage', label: 'Most used' },
  { id: 'updated', label: 'Recently updated' },
  { id: 'alpha', label: 'A – Z' },
];

export default function RfpLibraryPage() {
  const { user } = useAuth();
  const canManage = ['admin', 'super_admin'].includes(user?.role);
  const [blocks, setBlocks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [historyBlock, setHistoryBlock] = useState(null);
  const [detailBlock, setDetailBlock] = useState(null);
  const deepBlockRef = useRef(new URLSearchParams(window.location.search).get('block') || '');
  useEffect(() => {
    if (!deepBlockRef.current || !blocks.length) return;
    const b = blocks.find((x) => x.id === deepBlockRef.current);
    deepBlockRef.current = '';
    if (b) { setDetailBlock(b); setTimeout(() => document.querySelector(`[data-testid="rfp-row-${b.id}"]`)?.scrollIntoView({ block: 'center' }), 200); }
  }, [blocks]);
  const [dupPairs, setDupPairs] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [scrubOpen, setScrubOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // explorer state
  const [selectedCat, setSelectedCat] = useState('');
  const deepCatRef = useRef(new URLSearchParams(window.location.search).get('category') || '');
  useEffect(() => {
    if (!deepCatRef.current || !categories.length) return;
    const want = deepCatRef.current.toLowerCase();
    const hit = flattenTree(categories).find(c => (c.name || '').toLowerCase() === want);
    if (hit) setSelectedCat(hit.id);
    deepCatRef.current = '';
  }, [categories]);
  const [sort, setSort] = useState('capid');
  const [visible, setVisible] = useState(PAGE);
  // search
  const [q, setQ] = useState('');
  const [aiRank, setAiRank] = useState(false);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [aiRanked, setAiRanked] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/boilerplate`, { withCredentials: true });
      setBlocks(res.data?.blocks || []);
      setCategories(res.data?.categories || []);
    } catch {
      toast.error('Could not load the boilerplate library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManage) return;
    axios.get(`${API_URL}/api/rfp/boilerplate/duplicates`, { withCredentials: true })
      .then(res => setDupPairs(res.data?.pairs || []))
      .catch(() => {});
  }, [canManage, blocks.length]);

  useEffect(() => {
    if (!canManage) return;
    axios.get(`${API_URL}/api/rfp/boilerplate/health`, { withCredentials: true })
      .then(res => setHealth(res.data || null))
      .catch(() => {});
  }, [canManage, blocks]);

  const [promoOpen, setPromoOpen] = useState(false);
  const [promoPending, setPromoPending] = useState(0);
  const loadPromoCount = useCallback(() => {
    if (!canManage) return;
    axios.get(`${API_URL}/api/rfp/boilerplate/promotions`, { withCredentials: true })
      .then(res => setPromoPending((res.data?.promotions || []).length))
      .catch(() => {});
  }, [canManage]);
  useEffect(() => { loadPromoCount(); }, [loadPromoCount]);

  const [ovrOpen, setOvrOpen] = useState(false);
  const [ovrCount, setOvrCount] = useState(0);
  useEffect(() => {
    if (!canManage) return;
    axios.get(`${API_URL}/api/rfp/response/override-stats`, { withCredentials: true })
      .then(res => setOvrCount(res.data?.total_overrides || 0))
      .catch(() => {});
  }, [canManage]);
  useEffect(() => {
    if (!canManage) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('promotions')) setPromoOpen(true);
    if (sp.get('overrides')) setOvrOpen(true);
  }, [canManage]);

  const runSearch = useCallback(async (query, categoryId, useAi) => {
    if (!query.trim()) { setResults(null); setAiRanked(false); return; }
    setSearching(true);
    try {
      const params = { q: query.trim(), ai: useAi ? 1 : 0 };
      if (categoryId) params.category_id = categoryId;
      const res = await axios.get(`${API_URL}/api/rfp/boilerplate/search`, { params, withCredentials: true });
      setResults(res.data?.results || []);
      setAiRanked(!!res.data?.ai_ranked);
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q, selectedCat, aiRank), aiRank ? 700 : 350);
    return () => clearTimeout(debounceRef.current);
  }, [q, selectedCat, aiRank, runSearch]);

  useEffect(() => { setVisible(PAGE); }, [q, selectedCat, sort]);

  const copyBlock = async (b) => {
    const plain = (b.body || '')
      .replace(/<[^>]+>/g, '')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/^#{1,6}\s*/gm, '')
      .replace(/\*\*|__|`/g, '');
    try {
      await navigator.clipboard.writeText(plain);
      toast.success(`"${b.title}" copied to clipboard`);
    } catch {
      toast.error('Clipboard unavailable — select and copy the text manually');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/rfp/boilerplate/${b.id}/track-copy`, {}, { withCredentials: true });
      setBlocks(prev => prev.map(x => x.id === b.id ? { ...x, copy_count: (x.copy_count || 0) + 1 } : x));
    } catch { /* stats are best-effort */ }
  };

  const openCreate = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (b) => { setDetailBlock(null); setEditing(b); setEditorOpen(true); };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}" from the library?`)) return;
    try {
      await axios.delete(`${API_URL}/api/rfp/boilerplate/${b.id}`, { withCredentials: true });
      toast.success(`"${b.title}" deleted`);
      setDetailBlock(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete the block');
    }
  };

  const onDataChanged = () => {
    load();
    if (q.trim()) runSearch(q, selectedCat, aiRank); // refresh active search results too
    setTimeout(load, 6000); // pick up background keyword generation
  };

  const updateBlockMeta = async (b, fields) => {
    try {
      await axios.put(`${API_URL}/api/rfp/boilerplate/${b.id}/meta`, fields, { withCredentials: true });
      toast.success(fields.archived === true ? 'Capability archived' : fields.archived === false ? 'Capability restored'
        : fields.review_status === 'pending' ? 'Flagged for review' : 'Marked as confirmed');
      setDetailBlock(prev => (prev && prev.id === b.id ? { ...prev, ...fields } : prev));
      onDataChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };

  const exportLibrary = async (fmt) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/rfp/boilerplate/export/${fmt}`,
        { withCredentials: true, responseType: 'blob' });
      saveAs(res.data, `Seertech-RFP-Boilerplate-Library.${fmt}`);
      toast.success(`Library exported as ${fmt === 'docx' ? 'Word' : 'Excel'}`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const overlapMap = {};
  dupPairs.forEach(p => {
    (overlapMap[p.a.id] = overlapMap[p.a.id] || []).push(p.b.title);
    (overlapMap[p.b.id] = overlapMap[p.b.id] || []).push(p.a.title);
  });
  const catColors = Object.fromEntries(categories.filter(c => c.color).map(c => [c.name, c.color]));
  const topUsage = Math.max(0, ...blocks.map(b => (b.copy_count || 0) + (b.include_count || 0)));
  const searchActive = results !== null;
  const [fStatus, setFStatus] = useState('');
  const [fReview, setFReview] = useState('');
  const [fUpdated, setFUpdated] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fBy, setFBy] = useState('');
  const filtersActive = !!(fStatus || fReview || fUpdated || fBy);

  const applyFilters = useCallback((list) => {
    const now = Date.now();
    const days = (b) => (now - new Date(b.updated_at || 0).getTime()) / 86400000;
    const usage = (b) => (b.copy_count || 0) + (b.include_count || 0);
    return list.filter(b => {
      if (fStatus === 'archived') { if (!b.archived) return false; }
      else if (b.archived) return false;
      if (fStatus === 'new' && days(b) > 30) return false;
      if (fStatus === 'popular' && usage(b) < 2) return false;
      if (fStatus === 'stale' && days(b) <= 180) return false;
      if (fReview && (b.review_status || 'confirmed') !== fReview) return false;
      if (fUpdated === 'week' && days(b) > 7) return false;
      if (fUpdated === 'month' && days(b) > 31) return false;
      if (fUpdated === 'quarter' && days(b) > 92) return false;
      if (fUpdated === 'stale12' && days(b) <= 365) return false;
      if (fUpdated === 'custom') {
        const t = new Date(b.updated_at || 0).getTime();
        if (fFrom && t < new Date(fFrom).getTime()) return false;
        if (fTo && t > new Date(fTo).getTime() + 86400000) return false;
      }
      if (fBy && (b.updated_by || '') !== fBy) return false;
      return true;
    });
  }, [fStatus, fReview, fUpdated, fFrom, fTo, fBy]);

  const updatedByOptions = useMemo(() => [...new Set(blocks.map(b => b.updated_by).filter(x => x && x !== 'system'))].sort(), [blocks]);

  const browseList = useMemo(() => {
    let list = applyFilters(blocks);
    if (selectedCat) {
      const ids = descendantIds(categories, selectedCat);
      list = list.filter(b => (b.category_ids || []).some(c => ids.has(c)));
    }
    const usage = (b) => (b.copy_count || 0) + (b.include_count || 0);
    const sorted = [...list];
    if (sort === 'usage') sorted.sort((a, b) => usage(b) - usage(a));
    else if (sort === 'updated') sorted.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    else if (sort === 'alpha') sorted.sort((a, b) => a.title.localeCompare(b.title));
    else sorted.sort((a, b) => (a.capability_id || '').localeCompare(b.capability_id || ''));
    return sorted;
  }, [blocks, categories, selectedCat, sort, applyFilters]);

  const shown = searchActive ? applyFilters(results).slice(0, visible) : browseList.slice(0, visible);
  const totalCount = searchActive ? applyFilters(results).length : browseList.length;
  const tree = flattenTree(categories);

  // Keyboard navigation: arrows move the highlight, Enter opens the drawer.
  const [focusIdx, setFocusIdx] = useState(-1);
  const focusIdxRef = useRef(-1);
  const setFocus = (v) => { focusIdxRef.current = v; setFocusIdx(v); };
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const anyOverlay = editorOpen || catManagerOpen || importOpen || dupOpen || usageOpen || !!historyBlock || !!detailBlock;
  const overlayRef = useRef(anyOverlay);
  overlayRef.current = anyOverlay;
  useEffect(() => { setFocus(-1); }, [q, selectedCat, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => {
      if (overlayRef.current) return;
      const tag = (e.target.tagName || '').toLowerCase();
      const list = shownRef.current;
      if (!list.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (tag === 'textarea' || e.target.isContentEditable) return;
        if (tag === 'input') e.target.blur();
        e.preventDefault();
        const i = focusIdxRef.current;
        const next = e.key === 'ArrowDown' ? Math.min(i + 1, list.length - 1) : Math.max(i - 1, 0);
        setFocus(next);
        document.querySelector(`[data-testid="rfp-row-${list[next]?.id}"]`)?.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        if (tag === 'input' || tag === 'textarea' || tag === 'button' || e.target.isContentEditable) return;
        const i = focusIdxRef.current;
        if (i >= 0 && list[i]) setDetailBlock(list[i]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto" data-testid="rfp-library-page">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold text-[#0F2040] flex items-center gap-2">
            <BookMarked className="w-6 h-6 text-[#4ABFCF]" />
            RFP Capability Library
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Reusable Seertech capability and security answer blocks for annexures and question sheets — click any row for the full text. Longer narrative sections live in the Narrative Library.
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <AiJobsChip />
            {promoPending > 0 && (
              <button onClick={() => setPromoOpen(true)}
                className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 hover:bg-emerald-100 transition-colors"
                data-testid="rfp-library-promotions-chip"
                title="Answers promoted from RFPs by team members, waiting for an admin spot-check — revert any change in one click">
                <TrendingUp className="w-3 h-3" />
                {promoPending} promotion{promoPending === 1 ? '' : 's'} to review
              </button>
            )}
            {ovrCount > 0 && (
              <button onClick={() => setOvrOpen(true)}
                className="flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-1 hover:bg-indigo-100 transition-colors"
                data-testid="rfp-library-overrides-chip"
                title="How often reps override the AI's match strength, confidence and fit — spot where matching needs tuning">
                <Filter className="w-3 h-3" />
                {ovrCount} AI override{ovrCount === 1 ? '' : 's'}
              </button>
            )}
            {health && (
              <button onClick={() => setHealthOpen(true)}
                className={`flex items-center gap-1 text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${
                  health.score >= 80 ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                    : health.score >= 60 ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
                      : 'text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100'}`}
                data-testid="rfp-library-health-chip"
                title={`Library freshness score — ${health.stale} entr${health.stale === 1 ? 'y' : 'ies'} stale (>12 months), ${health.dup_pairs} duplicate pair${health.dup_pairs === 1 ? '' : 's'}, ${health.pending_recs} pending cleanup recommendation${health.pending_recs === 1 ? '' : 's'}. Actioning Clean & Consolidate recommendations improves it.`}>
                <HeartPulse className="w-3 h-3" />
                Health {health.score} · {health.label}
              </button>
            )}
            {health?.client_name_flags > 0 && (
              <button onClick={() => setScrubOpen(true)} className="flex items-center gap-1 text-[10px] font-semibold rounded-full px-2.5 py-1 border text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100" data-testid="rfp-library-scrub-chip" title="Blocks containing client names, pricing figures or named individuals — open the Tune-up queue">
                <Sparkles className="w-3 h-3" />{health.client_name_flags} sensitive-content flag{health.client_name_flags === 1 ? '' : 's'}
              </button>
            )}
            {dupPairs.length > 0 && (
              <button onClick={() => setDupOpen(true)}
                className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-100 transition-colors"
                data-testid="rfp-library-duplicates-chip"
                title="Capability pairs with heavily overlapping wording">
                <CopyX className="w-3 h-3" />
                {dupPairs.length} possible duplicate{dupPairs.length === 1 ? '' : 's'}
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={exporting} data-testid="rfp-library-export-btn">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportLibrary('docx')} data-testid="rfp-library-export-docx">
                  <FileText className="w-3.5 h-3.5 mr-2" />Word (.docx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportLibrary('xlsx')} data-testid="rfp-library-export-xlsx">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-2" />Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" onClick={() => setUsageOpen(true)} data-testid="rfp-library-usage-btn"
              title="Which capabilities get copied or packed most — and which are going stale">
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" />Usage
            </Button>
            <Button size="sm" variant="outline" onClick={() => setScrubOpen(true)} data-testid="rfp-library-scrub-btn"
              title="Tune-up: find customer / prospect names in library blocks and replace them with generic wording (library only — RFP responses untouched)">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />Tune-up
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCleanupOpen(true)}
              data-testid="rfp-library-cleanup-btn"
              title="AI review of the library — combine duplicates, retire superseded entries, improve drifted ones">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />Clean &amp; Consolidate
            </Button>
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
              data-testid="rfp-library-import-btn">
              <FileUp className="w-3.5 h-3.5 mr-1.5" />Import from Document
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCatManagerOpen(true)}
              data-testid="rfp-library-categories-btn">
              <FolderTree className="w-3.5 h-3.5 mr-1.5" />Categories
            </Button>
            <Button size="sm" onClick={openCreate} className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
              data-testid="rfp-library-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />Add Capability
            </Button>
          </div>
        )}
      </div>

      {/* Sticky search / sort bar */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm py-2 -mx-2 px-2 flex items-center gap-2 mb-3 flex-wrap border-b border-slate-100"
        data-testid="rfp-library-search-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search capabilities by keyword, category, ID or meaning…"
            className="pl-8 h-9 text-sm" data-testid="rfp-library-search-input" />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="rfp-library-search-clear"><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
        {/* Mobile fallback for the sidebar */}
        <Select value={selectedCat || '__all__'} onValueChange={v => setSelectedCat(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-44 h-9 text-xs md:hidden" data-testid="rfp-library-cat-filter">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All categories</SelectItem>
            {tree.map(c => (
              <SelectItem key={c.id} value={c.id}>{'\u00A0'.repeat(c.depth * 3)}{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44 h-9 text-xs" data-testid="rfp-library-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map(s => <SelectItem key={s.id} value={s.id}>Sort: {s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"
          title="Uses AI to rank matches semantically — a touch slower, smarter for tricky queries">
          <Switch checked={aiRank} onCheckedChange={setAiRank} data-testid="rfp-library-ai-toggle" />
          <Sparkles className="w-3.5 h-3.5 text-[#4ABFCF]" />AI rank
        </label>
        {searching && <Loader2 className="w-4 h-4 animate-spin text-[#4ABFCF]" />}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3 -mt-1" data-testid="rfp-library-filters">
        <Filter className={`w-3.5 h-3.5 ${filtersActive ? 'text-[#4ABFCF]' : 'text-slate-400'}`} />
        <Select value={fStatus || '__any__'} onValueChange={v => setFStatus(v === '__any__' ? '' : v)}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="rfp-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Status: any</SelectItem>
            <SelectItem value="new">New (30 days)</SelectItem>
            <SelectItem value="popular">Popular</SelectItem>
            <SelectItem value="stale">Stale (180+ days)</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fReview || '__any__'} onValueChange={v => setFReview(v === '__any__' ? '' : v)}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="rfp-filter-review">
            <SelectValue placeholder="Review status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Review: any</SelectItem>
            <SelectItem value="pending">Review pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fUpdated || '__any__'} onValueChange={v => setFUpdated(v === '__any__' ? '' : v)}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="rfp-filter-updated">
            <SelectValue placeholder="Last updated" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Updated: any time</SelectItem>
            <SelectItem value="week">Last week</SelectItem>
            <SelectItem value="month">Last month</SelectItem>
            <SelectItem value="quarter">Last quarter</SelectItem>
            <SelectItem value="stale12">Untouched &gt;12 months</SelectItem>
            <SelectItem value="custom">Date range…</SelectItem>
          </SelectContent>
        </Select>
        {fUpdated === 'custom' && (
          <>
            <input type="date" value={fFrom} onChange={e => setFFrom(e.target.value)}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 text-slate-600" data-testid="rfp-filter-from" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={fTo} onChange={e => setFTo(e.target.value)}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 text-slate-600" data-testid="rfp-filter-to" />
          </>
        )}
        <Select value={fBy || '__any__'} onValueChange={v => setFBy(v === '__any__' ? '' : v)}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="rfp-filter-by">
            <SelectValue placeholder="Updated by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Updated by: anyone</SelectItem>
            {updatedByOptions.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtersActive && (
          <button onClick={() => { setFStatus(''); setFReview(''); setFUpdated(''); setFFrom(''); setFTo(''); setFBy(''); }}
            className="text-xs text-slate-400 hover:text-slate-600 underline" data-testid="rfp-filter-clear">Clear filters</button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" /></div>
      ) : (
        <div className="flex gap-5 items-start">
          <div className="shrink-0 space-y-2">
            <CategoryTreeSidebar categories={categories} blocks={blocks}
              selected={selectedCat} onSelect={setSelectedCat} />
            <div className="space-y-1">
              {blocks.some(b => b.review_status === 'pending') && canManage && (
                <button onClick={() => { setFReview(fReview === 'pending' ? '' : 'pending'); setFStatus(''); }}
                  className={`w-full flex items-center justify-between text-xs rounded-md px-2.5 py-1.5 border transition-colors ${fReview === 'pending' ? 'bg-amber-50 border-amber-300 text-amber-800 font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  data-testid="rfp-sidebar-review-queue">
                  <span>Needs review</span>
                  <span className="text-[10px] bg-amber-100 text-amber-800 rounded-full px-1.5">{blocks.filter(b => b.review_status === 'pending' && !b.archived).length}</span>
                </button>
              )}
              {blocks.some(b => b.archived) && (
                <button onClick={() => { setFStatus(fStatus === 'archived' ? '' : 'archived'); setFReview(''); }}
                  className={`w-full flex items-center justify-between text-xs rounded-md px-2.5 py-1.5 border transition-colors ${fStatus === 'archived' ? 'bg-slate-100 border-slate-400 text-slate-800 font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  data-testid="rfp-sidebar-archived">
                  <span>Archived</span>
                  <span className="text-[10px] bg-slate-200 text-slate-700 rounded-full px-1.5">{blocks.filter(b => b.archived).length}</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 mb-2" data-testid="rfp-library-count">
              {searchActive ? (
                <>
                  {totalCount} match{totalCount === 1 ? '' : 'es'}
                  {aiRanked && <span className="ml-1.5 text-[#4ABFCF] font-medium"><Sparkles className="w-3 h-3 inline -mt-px" /> AI-ranked</span>}
                  {' '}— sorted by relevance
                </>
              ) : (
                <>{totalCount} capabilit{totalCount === 1 ? 'y' : 'ies'}{selectedCat ? ' in this category' : ''}</>
              )}
            </p>
            {shown.length === 0 ? (
              <p className="text-sm text-slate-500 py-10 text-center" data-testid="rfp-library-empty">
                {searchActive ? 'Nothing matches — try different keywords or switch on AI rank.' : 'No capabilities here yet.'}
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="rfp-library-list">
                {shown.map((b, i) => (
                  <BlockRow key={b.id} block={b} onOpen={setDetailBlock} onCopy={copyBlock}
                    onHistory={setHistoryBlock}
                    catColors={catColors} overlapWith={overlapMap[b.id]} topUsage={topUsage}
                    highlighted={i === focusIdx}
                    onRestore={canManage ? (blk) => updateBlockMeta(blk, { archived: false }) : null}
                    searchMeta={searchActive ? { score: b.score, matched: b.matched, ai_ranked: aiRanked } : null} />
                ))}
              </div>
            )}
            {totalCount > visible && (
              <div className="flex justify-center mt-3">
                <Button size="sm" variant="outline" onClick={() => setVisible(v => v + PAGE)}
                  data-testid="rfp-library-load-more">
                  Load more ({totalCount - visible} remaining)
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <BlockDetailDrawer block={detailBlock} onOpenChange={(v) => { if (!v) setDetailBlock(null); }}
        onMeta={canManage ? updateBlockMeta : null}
        canManage={canManage} catColors={catColors}
        onCopy={copyBlock} onEdit={openEdit} onDelete={remove}
        onHistory={(b) => { setDetailBlock(null); setHistoryBlock(b); }} />
      <BlockEditorDialog open={editorOpen} onOpenChange={setEditorOpen} editing={editing}
        categories={categories} onSaved={onDataChanged} />
      <CategoryManagerDialog open={catManagerOpen} onOpenChange={setCatManagerOpen}
        categories={categories} onChanged={onDataChanged} />
      <CleanupDialog open={cleanupOpen} onOpenChange={setCleanupOpen} categories={categories} onChanged={load} />
      <ScrubDialog open={scrubOpen} onOpenChange={setScrubOpen} canManage={canManage}
        onOpenHistory={(blockId) => { const b = blocks.find(x => x.id === blockId); if (b) { setScrubOpen(false); setHistoryBlock(b); } else toast.error('Block no longer exists'); }}
        onChanged={() => { load(); axios.get(`${API_URL}/api/rfp/boilerplate/health`, { withCredentials: true }).then(res => setHealth(res.data || null)).catch(() => {}); }} />
      <HealthDialog open={healthOpen} onOpenChange={setHealthOpen} health={health}
        onOpenCleanup={() => setCleanupOpen(true)} onFilterStale={() => setFUpdated('stale12')} />
      <ImportWizardDialog open={importOpen} onOpenChange={setImportOpen}
        categories={categories} onLibraryChanged={onDataChanged} />
      <BlockHistoryDialog block={historyBlock} onOpenChange={(v) => { if (!v) setHistoryBlock(null); }}
        canManage={canManage} onRestored={onDataChanged} />
      <PromotionsQueueDialog open={promoOpen} onOpenChange={setPromoOpen}
        onChanged={() => { loadPromoCount(); onDataChanged(); }} />
      <OverrideStatsDialog open={ovrOpen} onOpenChange={setOvrOpen} />
      <DuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} pairs={dupPairs}
        canManage={canManage} onMerged={onDataChanged} />
      <UsageTrendsDialog open={usageOpen} onOpenChange={setUsageOpen}
        canManage={canManage} onLibraryChanged={onDataChanged} />
    </div>
  );
}
